import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MegaphoneSvg from '../other/svg/MegaphoneSvg'
import PlusSvg from '../other/svg/PlusSvg'
import SecondaryButton from '../atoms/SecondaryButton'
import { Promotion } from 'src/redux/promotionsReducer'

interface VehiclePromotionPanelProps {
  promotions: Promotion[]
  onAddPromotion: (portal: string) => void
  onEditPromotion: (promotionId: number) => void
  onPausePromotion: (promotionId: number) => void
  onResumePromotion: (promotionId: number) => void
  onDeletePromotion: (promotionId: number) => void
}

const PORTALS = ['mobile.de']

const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

interface PortalMenuProps {
  portals: string[]
  existingPortals: string[]
  labelFor: (portal: string) => string
  onSelect: (portal: string) => void
  className: string
}

const PortalMenu = ({ portals, existingPortals, labelFor, onSelect, className }: PortalMenuProps): JSX.Element => {
  return (
    <div
      className={`w-44 p-1 absolute z-20 bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-start overflow-hidden ${className}`}
      style={{ boxShadow: menuShadow }}>
      {portals.map((portal) => {
        const exists = existingPortals.includes(portal)
        return (
          <button
            type="button"
            key={portal}
            disabled={exists}
            onClick={() => {
              onSelect(portal)
            }}
            className={`self-stretch px-2 py-1 rounded-sm inline-flex justify-start items-center ${
              exists
                ? 'bg-backgrounds-bg-field-component cursor-not-allowed'
                : 'bg-backgrounds-bg-field-component hover:bg-backgrounds-bg-field-component-hover'
            }`}>
            <div
              className={`flex-1 text-left justify-start text-xs font-medium font-Montserrat leading-5 ${
                exists ? 'text-foregrounds-fg-muted' : 'text-foregrounds-fg-base'
              }`}>
              {labelFor(portal)}
            </div>
          </button>
        )
      })}
    </div>
  )
}

const portalLabelKey = (portal: string): 'my_vehicles.promotion_mobile' | 'my_vehicles.promotion_otomoto' | null => {
  if (portal === 'mobile.de') {
    return 'my_vehicles.promotion_mobile'
  }
  if (portal === 'otomoto') {
    return 'my_vehicles.promotion_otomoto'
  }
  return null
}

const VehiclePromotionPanel = ({
  promotions,
  onAddPromotion,
  onEditPromotion,
  onPausePromotion,
  onResumePromotion,
  onDeletePromotion
}: VehiclePromotionPanelProps): JSX.Element => {
  const { t } = useTranslation()
  const isEmpty = promotions.length === 0
  const [menuAnchor, setMenuAnchor] = useState<'header' | 'empty' | null>(null)
  const headerMenuRef = useRef<HTMLDivElement>(null)
  const emptyMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      const headerContains = headerMenuRef.current?.contains(event.target as Node) === true
      const emptyContains = emptyMenuRef.current?.contains(event.target as Node) === true
      if (!headerContains && !emptyContains) {
        setMenuAnchor(null)
      }
    }

    if (menuAnchor !== null) {
      document.addEventListener('mousedown', handleClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [menuAnchor])

  const toggleMenu = (anchor: 'header' | 'empty'): void => {
    setMenuAnchor(menuAnchor === anchor ? null : anchor)
  }

  const existingPortals = promotions.map((promotion) => promotion.portal)

  const selectPortal = (portal: string): void => {
    if (existingPortals.includes(portal)) {
      return
    }
    setMenuAnchor(null)
    onAddPromotion(portal)
  }

  const labelFor = (portal: string): string => {
    const labelKey = portalLabelKey(portal)
    if (labelKey === null) {
      return portal
    }
    return t(labelKey)
  }

  return (
    <div className="flex-1 self-stretch bg-neutral-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base inline-flex flex-col justify-start items-start">
      <div className="self-stretch h-14 px-4 py-3 inline-flex justify-between items-center">
        <div className="justify-start text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5">
          {t('my_vehicles.promotion')}
        </div>
        <div className="relative" ref={headerMenuRef}>
          <SecondaryButton
            size="base"
            focused={menuAnchor === 'header'}
            onClick={() => {
              toggleMenu('header')
            }}
            icon={<PlusSvg className="w-3.5 h-3.5 text-icon-secondary" />}>
            {t('my_vehicles.add_promotion')}
          </SecondaryButton>
          {menuAnchor === 'header' && (
            <PortalMenu
              portals={PORTALS}
              existingPortals={existingPortals}
              labelFor={labelFor}
              onSelect={selectPortal}
              className="right-0 top-full"
            />
          )}
        </div>
      </div>
      <div className="self-stretch flex-1 px-3 pb-3 flex flex-col justify-start items-start">
        {isEmpty ? (
          <div className="self-stretch flex-1 p-6 bg-backgrounds-bg-field-component rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base flex flex-col justify-center items-center gap-6 overflow-visible">
            <div className="self-stretch flex flex-col justify-center items-center gap-3">
              <div className="size-8 flex justify-center items-center">
                <MegaphoneSvg className="size-8 text-icon-secondary" />
              </div>
              <div className="self-stretch flex flex-col justify-center items-center gap-1">
                <div className="self-stretch text-center justify-start text-foregrounds-fg-base text-xl font-semibold font-Montserrat leading-5">
                  {t('my_vehicles.promotion_empty_title')}
                </div>
                <div className="self-stretch text-center justify-start text-foregrounds-fg-subtle text-sm font-medium font-Montserrat leading-5">
                  {t('my_vehicles.promotion_empty_subtitle')}
                </div>
              </div>
            </div>
            <div className="relative pb-2 inline-flex justify-center items-center" ref={emptyMenuRef}>
              <SecondaryButton
                focused={menuAnchor === 'empty'}
                onClick={() => {
                  toggleMenu('empty')
                }}
                icon={<PlusSvg className="w-3.5 h-3.5 text-icon-secondary" />}>
                {t('my_vehicles.add_promotion')}
              </SecondaryButton>
              {menuAnchor === 'empty' && (
                <PortalMenu
                  portals={PORTALS}
                  existingPortals={existingPortals}
                  labelFor={labelFor}
                  onSelect={selectPortal}
                  className="left-1/2 top-full -translate-x-1/2"
                />
              )}
            </div>
          </div>
        ) : (
          <div className="self-stretch flex-1 relative bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-borders-border-base flex flex-col justify-start items-start overflow-hidden">
            {promotions.map((promotion, index) => (
              <React.Fragment key={promotion.portal}>
                {index > 0 && <div className="self-stretch h-px bg-borders-border-base" />}
                <div className="self-stretch h-11 px-4 inline-flex justify-start items-center gap-3 bg-backgrounds-bg-field-component">
                  <button
                    type="button"
                    onClick={() => {
                      onEditPromotion(promotion.id)
                    }}
                    className="flex-1 min-w-0 inline-flex justify-start items-center gap-3 text-left hover:opacity-80">
                    <span
                      className={`size-2 shrink-0 rounded-full ${
                        promotion.status === 'paused' ? 'bg-errorRed' : 'bg-successGreen'
                      }`}
                    />
                    <span className="truncate text-tag-neutral-tag-neutral-text text-xs font-semibold font-Montserrat leading-5">
                      {labelFor(promotion.portal)}
                    </span>
                  </button>
                  <span className="text-foregrounds-fg-subtle text-xs font-medium font-Montserrat leading-5 whitespace-nowrap">
                    {promotion.status === 'paused'
                      ? t('my_vehicles.promotion_paused')
                      : t('my_vehicles.promotion_listed')}
                  </span>
                  {typeof promotion.public_url === 'string' && promotion.public_url.length > 0 && (
                    <a
                      href={promotion.public_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-foregrounds-fg-base text-xs font-semibold font-Montserrat leading-5 whitespace-nowrap">
                      {t('my_vehicles.promotion_public_link')}
                    </a>
                  )}
                  {promotion.status === 'paused' ? (
                    <button
                      type="button"
                      onClick={() => {
                        onResumePromotion(promotion.id)
                      }}
                      className="text-foregrounds-fg-base text-xs font-semibold font-Montserrat leading-5 whitespace-nowrap">
                      {t('my_vehicles.promotion_resume')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        onPausePromotion(promotion.id)
                      }}
                      className="text-foregrounds-fg-base text-xs font-semibold font-Montserrat leading-5 whitespace-nowrap">
                      {t('my_vehicles.promotion_pause')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      onDeletePromotion(promotion.id)
                    }}
                    className="text-errorRed text-xs font-semibold font-Montserrat leading-5 whitespace-nowrap">
                    {t('my_vehicles.promotion_delete')}
                  </button>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default VehiclePromotionPanel
